import axios from "axios";
import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useQueryScope } from "@/hooks/use-query-scope";
import { useScopedQuery } from "@/hooks/use-scoped-query";
import {
  adminTeammatesKey,
  adminTeammatesQuery,
  myTeammatesKey,
  myTeammatesQuery,
} from "@/queries/teammates";
import { useStore } from "@/stores/store-context";
import type {
  TAdminTeammate,
  TAdminTeammatesScreen,
  TMemberTeammate,
  TMemberTeammatesScreen,
} from "@/types/teammates";

/**
 * The stable `code` of a refused settings write (`{detail, code}`, e.g.
 * `ai_features_off`, `consent_version_changed`), or `null` when the failure
 * carried none (a network error, a 500, a field-error 400).
 */
export function teammateSettingsErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const code = (error.response?.data as { code?: unknown } | undefined)?.code;
  return typeof code === "string" ? code : null;
}

/** A screen with one teammate's row replaced through `change`. */
function withTeammate<TRow extends { slug: string }, TScreen extends { teammates: TRow[] }>(
  screen: TScreen,
  slug: string,
  change: (row: TRow) => TRow,
): TScreen {
  return {
    ...screen,
    teammates: screen.teammates.map((row) => (row.slug === slug ? change(row) : row)),
  };
}

/** Apply `change` to one row of the cached screen under `key`, if it is cached. */
function patchRow<TRow extends { slug: string }, TScreen extends { teammates: TRow[] }>(
  client: QueryClient,
  key: QueryKey,
  slug: string,
  change: (row: TRow) => TRow,
): void {
  client.setQueryData<TScreen>(key, (screen) =>
    screen ? withTeammate<TRow, TScreen>(screen, slug, change) : screen,
  );
}

/**
 * What the admin's switch shows the moment it is flipped. Switching on is the
 * admin's consent to the current version, so a teammate that was waiting on it
 * stops waiting; switching off consents to nothing and changes only the switch.
 */
export function optimisticGrant(row: TAdminTeammate, enabled: boolean): TAdminTeammate {
  if (!enabled) return { ...row, enabled: false };
  return {
    ...row,
    enabled: true,
    needs_reconsent: false,
    consented_version: row.consent_version,
  };
}

export interface AdminTeammatesRead {
  /** This workspace's admin screen, once read. Never another workspace's. */
  readonly screen: TAdminTeammatesScreen | undefined;
  /** The read failed, nothing is cached and no retry is running. */
  readonly failed: boolean;
  /** Read again after a failure. */
  readonly reload: () => void;
  /**
   * Switch a teammate on or off for the workspace, shown at once. Rejects when
   * the server refuses, with the row already put back; the caller tells the
   * user. Switching on (also the "Approve" of a teammate needing re-consent)
   * records the admin's consent to its current version.
   */
  readonly setEnabled: (teammateSlug: string, enabled: boolean) => Promise<TAdminTeammate>;
}

/**
 * The admin's Digital Teammates screen and its switch (NG-2622), for Workspace
 * settings → Digital Teammates.
 *
 * Optimistic: the switch moves in the cache before the request leaves, and the
 * row goes back to what it was if the server refuses. Only that row is put
 * back, so a refused write never undoes another teammate's switch. Writes for
 * one workspace run one at a time, in the order they were made, so the server
 * keeps the last one the admin made.
 *
 * Must be called from an `observer` component.
 */
export function useAdminTeammates(workspaceSlug: string): AdminTeammatesRead {
  const { workspaceTeammatesService } = useStore();
  const client = useQueryClient();
  const { mode, query } = useScopedQuery(workspaceSlug, (scope) =>
    adminTeammatesQuery(workspaceTeammatesService, scope),
  );
  const scope = useQueryScope(workspaceSlug);

  const mutation = useMutation({
    scope: { id: `teammates-admin:${workspaceSlug}` },
    mutationFn: ({ teammateSlug, enabled }: { teammateSlug: string; enabled: boolean }) =>
      workspaceTeammatesService.updateGrant(workspaceSlug, teammateSlug, { enabled }),
    onSuccess: (row) => {
      if (scope) patchRow(client, adminTeammatesKey(scope), row.slug, () => row);
    },
  });

  async function setEnabled(teammateSlug: string, enabled: boolean): Promise<TAdminTeammate> {
    const key = scope ? adminTeammatesKey(scope) : null;
    const before = key
      ? client
          .getQueryData<TAdminTeammatesScreen>(key)
          ?.teammates.find((row) => row.slug === teammateSlug)
      : undefined;
    if (key) {
      // A read already out left before this write and would land without it.
      await client.cancelQueries({ queryKey: key, exact: true });
      patchRow<TAdminTeammate, TAdminTeammatesScreen>(client, key, teammateSlug, (row) =>
        optimisticGrant(row, enabled),
      );
    }
    try {
      return await mutation.mutateAsync({ teammateSlug, enabled });
    } catch (error) {
      console.warn("[teammates] the switch was refused; putting the row back", {
        workspaceSlug,
        teammateSlug,
        enabled,
        error,
      });
      if (key && before) patchRow(client, key, teammateSlug, () => before);
      throw error;
    }
  }

  return {
    screen: mode === "cache" ? query.data : undefined,
    failed: mode === "cache" && query.isError && !query.isFetching && query.data === undefined,
    reload: () => {
      void query.refetch();
    },
    setEnabled,
  };
}

/** Refusals of a member's consent that mean the screen they saw is out of date:
 *  the teammate's version moved on, or it now waits on the admin's approval. */
const STALE_SCREEN_CODES = new Set(["consent_version_changed", "waiting_for_admin"]);

/** Whether a refused consent means the member's screen is out of date, so it is
 *  re-read and they are told the teammate changed. The one place that decides it:
 *  the hook's re-read and the panel's message both ask here. */
export function isStaleScreenRefusal(error: unknown): boolean {
  return STALE_SCREEN_CODES.has(teammateSettingsErrorCode(error) ?? "");
}

export interface MyTeammatesRead {
  /** The caller's screen for this workspace, once read. */
  readonly screen: TMemberTeammatesScreen | undefined;
  /** The read failed, nothing is cached and no retry is running. */
  readonly failed: boolean;
  /** Read again after a failure. */
  readonly reload: () => void;
  /**
   * Turn the caller's own teammate on, consenting to exactly the version the
   * screen showed them. Waits for the server, because the server may refuse
   * the consent; a refusal that means the screen is stale re-reads it.
   */
  readonly turnOn: (teammate: TMemberTeammate) => Promise<TMemberTeammate>;
  /** Turn the caller's own teammate off: shown at once, put back if refused. */
  readonly turnOff: (teammateSlug: string) => Promise<TMemberTeammate>;
}

/**
 * The member's Digital Teammates screen (NG-2622), for Profile → Digital
 * Teammates: the teammates acting in their name here, and the writes that turn
 * their own on and off.
 *
 * Turning one on is a consent, which the server checks against the current
 * version, so it is not optimistic: the row changes when the server agrees.
 * Turning one off is, and the row goes back if the server refuses.
 *
 * Must be called from an `observer` component.
 */
export function useMyTeammates(workspaceSlug: string): MyTeammatesRead {
  const { workspaceTeammatesService } = useStore();
  const client = useQueryClient();
  const { mode, query } = useScopedQuery(workspaceSlug, (scope) =>
    myTeammatesQuery(workspaceTeammatesService, scope),
  );
  const scope = useQueryScope(workspaceSlug);
  const key = scope ? myTeammatesKey(scope) : null;

  const mutation = useMutation({
    scope: { id: `teammates-mine:${workspaceSlug}` },
    mutationFn: ({
      teammateSlug,
      active,
      consentVersion,
    }: {
      teammateSlug: string;
      active: boolean;
      consentVersion?: number;
    }) =>
      workspaceTeammatesService.updateMine(
        workspaceSlug,
        teammateSlug,
        active ? { active, consent_version: consentVersion } : { active },
      ),
    onSuccess: (row) => {
      if (key) patchRow(client, key, row.slug, () => row);
    },
  });

  async function turnOn(teammate: TMemberTeammate): Promise<TMemberTeammate> {
    try {
      return await mutation.mutateAsync({
        teammateSlug: teammate.slug,
        active: true,
        consentVersion: teammate.consent_version,
      });
    } catch (error) {
      console.warn("[teammates] turning a teammate on was refused", {
        workspaceSlug,
        teammateSlug: teammate.slug,
        consentVersion: teammate.consent_version,
        error,
      });
      if (key && isStaleScreenRefusal(error)) {
        client.invalidateQueries({ queryKey: key, exact: true }).catch((reread: unknown) => {
          console.warn("[teammates] re-reading the screen after a refused consent failed", {
            workspaceSlug,
            error: reread,
          });
        });
      }
      throw error;
    }
  }

  async function turnOff(teammateSlug: string): Promise<TMemberTeammate> {
    const before = key
      ? client
          .getQueryData<TMemberTeammatesScreen>(key)
          ?.teammates.find((row) => row.slug === teammateSlug)
      : undefined;
    if (key) {
      await client.cancelQueries({ queryKey: key, exact: true });
      patchRow<TMemberTeammate, TMemberTeammatesScreen>(client, key, teammateSlug, (row) => ({
        ...row,
        state: "paused",
      }));
    }
    try {
      return await mutation.mutateAsync({ teammateSlug, active: false });
    } catch (error) {
      console.warn("[teammates] turning a teammate off was refused; putting the row back", {
        workspaceSlug,
        teammateSlug,
        error,
      });
      if (key && before) patchRow(client, key, teammateSlug, () => before);
      throw error;
    }
  }

  return {
    screen: mode === "cache" ? query.data : undefined,
    failed: mode === "cache" && query.isError && !query.isFetching && query.data === undefined,
    reload: () => {
      void query.refetch();
    },
    turnOn,
    turnOff,
  };
}
